import { create } from 'zustand';

export interface AlertAction {
  label: string;
  onClick: () => void;
  primary?: boolean;
}

export interface AppAlert {
  id: string;
  type: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message?: string;
  actions?: AlertAction[];
  duration?: number; // 0 for infinite
}

interface AlertStore {
  alerts: AppAlert[];
  addAlert: (alert: Omit<AppAlert, 'id'>) => string;
  removeAlert: (id: string) => void;
}

export const useAlertStore = create<AlertStore>((set) => ({
  alerts: [],
  addAlert: (alert) => {
    const id = Math.random().toString(36).substring(2, 9);
    set((state) => ({
      alerts: [...state.alerts, { ...alert, id }],
    }));
    if (alert.duration !== 0) {
      setTimeout(() => {
        set((state) => ({
          alerts: state.alerts.filter((a) => a.id !== id),
        }));
      }, alert.duration || 5000);
    }
    return id;
  },
  removeAlert: (id) =>
    set((state) => ({ alerts: state.alerts.filter((a) => a.id !== id) })),
}));

export const alertService = {
  show: (alert: Omit<AppAlert, 'id'>) => useAlertStore.getState().addAlert(alert),
  success: (title: string, message?: string, actions?: AlertAction[], duration?: number) =>
    useAlertStore.getState().addAlert({ type: 'success', title, message, actions, duration }),
  error: (title: string, message?: string, actions?: AlertAction[], duration?: number) =>
    useAlertStore.getState().addAlert({ type: 'error', title, message, actions, duration }),
  info: (title: string, message?: string, actions?: AlertAction[], duration?: number) =>
    useAlertStore.getState().addAlert({ type: 'info', title, message, actions, duration }),
  warning: (title: string, message?: string, actions?: AlertAction[], duration?: number) =>
    useAlertStore.getState().addAlert({ type: 'warning', title, message, actions, duration }),
  dismiss: (id: string) => useAlertStore.getState().removeAlert(id),
};
